<template>
    <div
        class="flex w-full flex-col items-center gap-6"
        :class="{ 'min-h-0 flex-1 justify-center': onlyMobileVisible }"
        role="status"
        aria-live="polite"
    >
        <span class="sr-only">{{ resultText }}</span>

        <div class="mb-2 mt-3 flex w-full items-center justify-between gap-3 px-1">
            <div class="flex flex-col gap-0.5">
                <span class="text-base font-semibold text-theme-accent">{{ resultHeadline }}</span>
                <span class="text-xs text-theme-toolbar-icon">{{ resultSubtitle }}</span>
            </div>
            <span class="text-[40px] font-bold leading-none text-theme-text"
                >{{ studyResults.know }}/{{ studyResults.total }}</span
            >
        </div>

        <div v-if="unknownCards.length" class="mb-2 flex w-full flex-col gap-1.5 px-1">
            <span class="text-xs font-semibold uppercase tracking-wide text-theme-toolbar-icon">{{
                localizations[lang].flashcard.studyReviewLabel
            }}</span>
            <ul class="flex w-full flex-col gap-1.5">
                <li
                    v-for="card in unknownCards"
                    :key="card.id"
                    class="truncate rounded-lg border bg-theme-bg px-3 py-2 text-sm text-theme-text"
                    :style="reviewItemStyle(card)"
                >
                    {{ card.frontText }}
                </li>
            </ul>
        </div>

        <div class="flex w-full flex-col items-stretch gap-2.5">
            <BaseButton v-if="studyResults.dontKnow > 0" variant="primary" @click="studyOnlyUnknown">
                {{ localizations[lang].flashcard.studyDontKnow }}
            </BaseButton>
            <BaseButton :variant="studyResults.dontKnow > 0 ? 'secondary' : 'primary'" @click="restartStudy">
                {{ localizations[lang].flashcard.studyAgain }}
            </BaseButton>
        </div>

        <BaseButton variant="ghost" @click="endStudyMode">
            {{ localizations[lang].flashcard.endStudy }}
        </BaseButton>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapState } from 'vuex';
import BaseButton from '../Components/buttons/BaseButton.vue';

export default {
    name: 'FlashcardStudyResults',
    components: { BaseButton },
    computed: {
        ...mapState(['localizations', 'lang', 'flashcardStudyMode']),
        ...mapGetters(['allHighlighters', 'onlyMobileVisible', 'studyResults']),
        /**
         * @returns {number} Percentage of cards rated 'know' this session (0 when empty)
         */
        scorePercent() {
            const { know, total } = this.studyResults;
            return total ? Math.round((know / total) * 100) : 0;
        },
        /**
         * @returns {string} Localized "Result: {know} of {total} ({percent}%)" summary text
         */
        resultText() {
            const { know, total } = this.studyResults;
            return this.localizations[this.lang].flashcard.studyResult
                .replace('{know}', know)
                .replace('{total}', total)
                .replace('{percent}', this.scorePercent);
        },
        /**
         * @returns {'Perfect'|'Great'|'Keep'} Encouragement tier derived from the score, used to
         * build the matching headline/subtitle i18n keys
         */
        resultTier() {
            if (this.scorePercent >= 100) return 'Perfect';
            if (this.scorePercent >= 60) return 'Great';
            return 'Keep';
        },
        /**
         * @returns {string} Localized adaptive headline for the current score tier
         */
        resultHeadline() {
            return this.localizations[this.lang].flashcard[`studyDone${this.resultTier}`];
        },
        /**
         * @returns {string} Localized encouraging subtitle for the current score tier
         */
        resultSubtitle() {
            return this.localizations[this.lang].flashcard[`studyDone${this.resultTier}Hint`];
        },
        /**
         * @returns {Array<Object>} Cards rated 'dontKnow' this session, listed in the summary so
         * the user sees which fronts to review
         */
        unknownCards() {
            const ratings = this.flashcardStudyMode.ratings;
            return this.flashcardStudyMode.cards.filter(card => ratings[card.id] === 'dontKnow');
        },
    },
    methods: {
        ...mapActions(['startStudyMode', 'endStudyMode']),
        /**
         * Resolves a card's hex color from its slug (same logic as FlashcardCard), falling back
         * to its inline color.
         * @param {{colorSlug?: string, color?: string}} card
         * @returns {string} Hex color
         */
        resolveCardColor(card) {
            const matchedColor = this.allHighlighters.find(color => color.slug === card.colorSlug);
            return matchedColor ? matchedColor.color : card.color;
        },
        /**
         * Inline chip style for a "to review" card: only a border in the card's color; the fill
         * stays the plain theme surface (white in light themes).
         * @param {Object} card
         * @returns {{borderColor: string}}
         */
        reviewItemStyle(card) {
            return { borderColor: `${this.resolveCardColor(card)}66` };
        },
        /**
         * Restarts the session with the full set, clearing all per-session ratings.
         */
        restartStudy() {
            this.startStudyMode();
        },
        /**
         * Starts a new session containing only the cards rated 'dontKnow' in this session.
         */
        studyOnlyUnknown() {
            this.startStudyMode({ onlyUnknown: true });
        },
    },
};
</script>
